import { graphql, useStaticQuery } from "gatsby";
import React from "react";
import Image from "../components/GatsbyImage";
import { getImage } from "gatsby-plugin-image";
import { OutboundLink } from "gatsby-plugin-google-gtag";
import InternalLink from "./InternalLink";
import ExternalLink from "./ExternalLink";
import useUnileverLogo from "../hooks/useUnileverLogo";
import SocialChannels from "./SocialChannels";
import useCurrentYear from "../hooks/useCurrentYear";
import LanguageSelector from "./LanguageSelector";
import { RichTextRenderer } from "./RichTextRenderer";
import { convertToHTMLPath } from "../templates/sitemap/utils";
import "../graphql-fragments";

const STATIC_QUERY = graphql`
  query GetFooterData {
    home {
      faq {
        path
        title
      }
      disclaimerMessage {
        path
        title
        teaserImage {
          ...MessageCardMedia
        }
      }
      footerLinks {
        ... on Hyperlink {
          id
          title
          url
        }
        ... on Product {
          id
          title: name
          slug
        }
        ... on ContactUs {
          id
          title
          slug
        }
        ... on message {
          id
          title
          slug
        }
        ... on topic {
          id
          title
          slug
        }
        ... on SustainabilityCategory {
          id
          title
          slug
        }
        ... on ContentfulCategory {
          id
          title: pageTitle
          slug
        }
        ... on SustainabilityLandingPage {
          id
          title
          slug
        }
        ... on ContentfulArticle {
          id
          title: pageTitle
          slug
        }
      }
      additionalExternalLinks {
        id
        title
        url
      }
      legals {
        additionalMessages {
          id
          slug
          title
        }
      }
    }
    sitemap {
      path
      title
    }
  }
`;

const Footer = ({
  home: {
    brand: { disclaimer },
    legals,
    socialChannels,
    lexicon,
  },
  hideDisclaimerMessage,
}) => {
  const data = useStaticQuery(STATIC_QUERY);
  const currentYear = useCurrentYear();
  const logo = useUnileverLogo();
  const {
    cookieNoticeLabel,
    cookieNoticeUrl,
    privacyNoticeLabel,
    privacyNoticeUrl,
    termsOfUseLabel,
    termsOfUseUrl,
    whatsInOurProductsLabel,
    whatsInOurProductsUrl,
    protectionOfPersonalDataLabel,
    protectionOfPersonalDataUrl,
    legalWarningLabel,
    legalWarningUrl,
    blurb,
  } = legals || {};

  const {
    home: { disclaimerMessage, footerLinks },
  } = data;
  return (
    <>
      {!hideDisclaimerMessage && disclaimerMessage && (
        <aside className="p-5 bg-white md:py-20">
          <InternalLink
            href={disclaimerMessage.path}
            className="block max-w-md mx-auto"
          >
            <Image
              image={getImage(disclaimerMessage.teaserImage?.file.fileLocal)}
              alt={disclaimerMessage.teaserImage?.description}
            />
          </InternalLink>
        </aside>
      )}
      <footer>
        <div className="py-10 lg:pt-48">
          <div className="container px-8 mx-auto footer-links">
            <div className="md:flex md:justify-center">
              <OutboundLink
                href="https://www.unilever.com"
                className="inline-block mb-10 unilever-logo md:mr-10"
                title="Unilever"
                rel="noopener noreferrer nofollow"
              >
                <Image
                  image={getImage(logo)}
                  style={{ width: 100, height: 108 }}
                  alt="Unilever logo"
                />
              </OutboundLink>
              <ul className="mb-10 grid grid-cols-2 gap-6 md:min-w-sm">
                {footerLinks &&
                  footerLinks.map((link) => {
                    const href = link?.slug
                      ? convertToHTMLPath(link.slug)
                      : link.url;
                    const external = link.url && link.url.includes("http");
                    return (
                      <FooterLink href={href} external={external} key={link.id}>
                        {link.title}
                      </FooterLink>
                    );
                  })}
                {data.home.faq && (
                  <FooterLink href={data.home.faq.path}>
                    {data.home.faq.title}
                  </FooterLink>
                )}
                {cookieNoticeUrl && cookieNoticeLabel && (
                  <FooterLink href={cookieNoticeUrl} id="_bapw-link" external>
                    {cookieNoticeLabel}
                  </FooterLink>
                )}
                {privacyNoticeUrl && privacyNoticeLabel && (
                  <FooterLink href={privacyNoticeUrl} external>
                    {privacyNoticeLabel}
                  </FooterLink>
                )}
                {termsOfUseUrl && termsOfUseLabel && (
                  <FooterLink href={termsOfUseUrl} external>
                    {termsOfUseLabel}
                  </FooterLink>
                )}
                {whatsInOurProductsUrl && whatsInOurProductsLabel && (
                  <FooterLink href={whatsInOurProductsUrl} external>
                    {whatsInOurProductsLabel}
                  </FooterLink>
                )}
                {protectionOfPersonalDataUrl && protectionOfPersonalDataLabel && (
                  <FooterLink href={protectionOfPersonalDataUrl} external>
                    {protectionOfPersonalDataLabel}
                  </FooterLink>
                )}
                {legalWarningUrl && legalWarningLabel && (
                  <FooterLink href={legalWarningUrl} external>
                    {legalWarningLabel}
                  </FooterLink>
                )}
                {data.home.legals?.additionalMessages?.map((message) => (
                  <FooterLink key={message.id} href={`${message.slug}.html`}>
                    {message.title}
                  </FooterLink>
                ))}
                {data.home.additionalExternalLinks.map((message) => (
                  <FooterLink key={message.id} href={message.url} external>
                    {message.title}
                  </FooterLink>
                ))}
                {data.sitemap && (
                  <FooterLink href={data.sitemap.path}>
                    {data.sitemap.title}
                  </FooterLink>
                )}
              </ul>
            </div>
            <LanguageSelector />
          </div>
          <SocialChannels {...{ lexicon, socialChannels }} />
          <OutboundLink
            href="https://www.unilever.com"
            className="container block px-8 mx-auto text-lg font-bold text-center unilever-copyright"
            title="Unilever"
            rel="noopener noreferrer nofollow"
          >
            © Unilever {currentYear}
          </OutboundLink>
          {blurb?.json && (
            <RichTextRenderer
              assets={[]}
              className="px-20 pt-5 mt-20 text-xs text-center whitespace-pre-wrap border-t border-grey-light legal-blurb inline-html"
              component="section"
              rawContent={JSON.parse(blurb.json)}
            />
          )}
        </div>
        {!disclaimerMessage && disclaimer && (
          <p className="p-10 text-xl text-center disclaimer bg-yellow text-brown">
            {disclaimer}
          </p>
        )}
      </footer>
    </>
  );
};

function FooterLink({ children, href, id, external }) {
  const LinkComponent = external ? ExternalLink : InternalLink;
  return (
    <li className="block underline">
      <LinkComponent href={href} id={id}>
        {children}
      </LinkComponent>
    </li>
  );
}

export default Footer;
